import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  List,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Avatar,
  Tag,
  Space,
  Tooltip,
  Typography,
  Row,
  Col,
  Badge,
  Divider,
  Alert,
  Spin,
  Empty,
  Popconfirm,
  Upload,
  Dropdown,
  Menu,
} from "antd";
import {
  MessageOutlined,
  PlusOutlined,
  UserOutlined,
  PushpinOutlined,
  LockOutlined,
  EyeOutlined,
  LikeOutlined,
  SendOutlined,
  PaperClipOutlined,
  SmileOutlined,
  MoreOutlined,
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  SecurityScanOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { AppContext } from "../context/AppContext";
import { useDiscussionNotifications } from "../components/DiscussionNotificationProvider";
import discussionService from "../utils/discussionService";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const DiscussionForum = () => {
  const { company, user } = useContext(AppContext);
  const { discussionService: notificationService } = useDiscussionNotifications();
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [isTopicModalVisible, setIsTopicModalVisible] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [form] = Form.useForm();
  const token = localStorage.getItem("token");

  const categories = [
    { value: "general", label: "General Discussion", color: "blue" },
    { value: "announcements", label: "Announcements", color: "orange" },
    { value: "support", label: "Support", color: "green" },
    { value: "feedback", label: "Feedback", color: "purple" },
  ];



  useEffect(() => {
    if (company && user && token) {
      fetchTopics();
      fetchOnlineUsers();
      setupRealTimeListeners();
    }
    
    return () => {
      // Cleanup listeners
      discussionService.removeEventListener('new_message', handleNewMessage);
      discussionService.removeEventListener('new_topic', handleNewTopic);
      discussionService.removeEventListener('presence_update', handlePresenceUpdate);
    };
  }, [company, user, token]);

  const fetchTopics = async () => {
    if (!company || !token) return;
    
    setLoading(true);
    try {
      const topicsData = await discussionService.getTopics(company.id, token);
      
      // Transform topics data to match expected format
      const transformedTopics = topicsData.map(topic => ({
        ...topic,
        created_by: topic.user_created || {
          id: topic.user_created,
          first_name: 'Unknown',
          last_name: 'User',
          avatar: null
        },
        created_at: topic.date_created || topic.created_at
      }));
      
      setTopics(transformedTopics);
    } catch (error) {
      message.error('Failed to fetch topics');
      console.error('Error fetching topics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (topicId) => {
    if (!token) return;
    
    setMessagesLoading(true);
    try {
      const messagesData = await discussionService.getMessages(topicId, token);
      
      // Transform messages data to match expected format
      const transformedMessages = messagesData.map(msg => ({
        ...msg,
        created_at: msg.date_created,
        user: msg.user || {
          first_name: 'Unknown',
          last_name: 'User',
          avatar: null
        }
      }));
      
      setMessages(transformedMessages);
      
      // Mark topic as read
      if (user) {
        await discussionService.markTopicAsRead(topicId, user.id, token);
      }
    } catch (error) {
      message.error('Failed to fetch messages');
      console.error('Error fetching messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  const fetchOnlineUsers = async () => {
    if (!company || !token) return;
    
    try {
      const onlineUsersData = await discussionService.getOnlineUsers(company.id, token);
      setOnlineUsers(onlineUsersData.map(user => ({
        id: user.user_id,
        name: `${user.user.first_name} ${user.user.last_name}`,
        status: user.status
      })));
    } catch (error) {
      console.error('Error fetching online users:', error);
    }
  };

  const handleCreateTopic = async (values) => {
    if (!company || !user || !token) return;
    
    try {
      const topicData = {
        title: values.title,
        description: values.description,
        category: values.category,
        created_by: user.id,
        company: company.id,
        status: "active",
        is_pinned: false,
        is_locked: false,
        message_count: 0
      };
      
      const newTopic = await discussionService.createTopic(topicData, token);
      
      // Add to local state
      setTopics([newTopic, ...topics]);
      
      setIsTopicModalVisible(false);
      form.resetFields();
      message.success("Topic created successfully!");
      
      // Broadcast new topic
      discussionService.sendMessage({
        type: 'new_topic',
        topic: newTopic
      });
    } catch (error) {
      message.error("Failed to create topic");
      console.error('Error creating topic:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedTopic || !user || !token) return;

    try {
      const messageData = {
        topic_id: selectedTopic.id,
        user: user.id,
        message: newMessage.trim(),
        message_type: "text"
      };

      const sentMessage = await discussionService.sendChatMessage(messageData, token);
      
      // Transform and add to local messages
      const transformedSentMessage = {
        ...sentMessage,
        created_at: sentMessage.date_created,
        user: sentMessage.user || user
      };
      
      setMessages([...messages, transformedSentMessage]);
      
      // Clear input
      setNewMessage("");
      
      // Stop typing indicator
      discussionService.sendTypingIndicator(selectedTopic.id, user.id, false);
      
      message.success("Message sent!");
    } catch (error) {
      message.error("Failed to send message");
      console.error('Error sending message:', error);
    }
  };

  const getCategoryColor = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.color : "default";
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  // Real-time event handlers
  const setupRealTimeListeners = () => {
    discussionService.addEventListener('new_message', handleNewMessage);
    discussionService.addEventListener('new_topic', handleNewTopic);
    discussionService.addEventListener('presence_update', handlePresenceUpdate);
    discussionService.addEventListener('typing', handleTypingIndicator);
  };

  const handleNewMessage = (data) => {
    const { message, topic_id } = data;
    
    // Transform message data to match expected format
    const transformedMessage = {
      ...message,
      created_at: message.date_created,
      user: message.user || {
        first_name: 'Unknown',
        last_name: 'User',
        avatar: null
      }
    };
    
    // Update messages if viewing the topic
    if (selectedTopic && selectedTopic.id === topic_id) {
      setMessages(prev => [...prev, transformedMessage]);
    }
    
    // Update topic message count
    setTopics(prev => prev.map(topic => 
      topic.id === topic_id 
        ? { ...topic, message_count: topic.message_count + 1, last_activity: message.date_created }
        : topic
    ));
  };

  const handleNewTopic = (data) => {
    const { topic } = data;
    
    // Transform the topic data to match expected format
    const transformedTopic = {
      ...topic,
      created_by: topic.user_created || {
        id: topic.user_created,
        first_name: user?.first_name || 'Unknown',
        last_name: user?.last_name || 'User',
        avatar: null
      },
      created_at: topic.date_created
    };
    
    setTopics(prev => [transformedTopic, ...prev]);
  };

  const handlePresenceUpdate = (data) => {
    const { user_id, status } = data;
    fetchOnlineUsers(); // Refresh online users list
  };

  const handleTypingIndicator = (data) => {
    const { topic_id, user_id, is_typing } = data;
    // Handle typing indicators - could show "User is typing..." message
    console.log(`User ${user_id} is ${is_typing ? 'typing' : 'stopped typing'} in topic ${topic_id}`);
  };

  // Add typing indicator when user types
  const handleMessageInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    
    if (selectedTopic && user) {
      // Send typing indicator
      discussionService.sendTypingIndicator(selectedTopic.id, user.id, value.length > 0);
    }
  };

  const renderTopicsList = () => (
    <List
      itemLayout="vertical"
      dataSource={topics}
      renderItem={(topic) => (
        <List.Item
          key={topic.id}
          actions={[
            <Space key="stats">
              <MessageOutlined />
              <span>{topic.message_count} messages</span>
            </Space>,
            <span key="activity">
              Last activity: {dayjs(topic.last_activity).fromNow()}
            </span>
          ]}
          onClick={() => {
            setSelectedTopic(topic);
            fetchMessages(topic.id);
            // Update presence to show user is viewing this topic
            if (user && company) {
              discussionService.updateUserPresence(user.id, company.id, 'online', topic.id, token);
            }
          }}
          style={{ cursor: "pointer" }}
        >
          <List.Item.Meta
            avatar={
              <Avatar
                icon={<UserOutlined />}
                src={topic.created_by?.avatar}
              />
            }
            title={
              <Space>
                {topic.is_pinned && <PushpinOutlined style={{ color: "#faad14" }} />}
                {topic.is_locked && <LockOutlined style={{ color: "#ff4d4f" }} />}
                <span>{topic.title}</span>
                <Tag color={getCategoryColor(topic.category)} size="small">
                  {getCategoryLabel(topic.category)}
                </Tag>
              </Space>
            }
            description={
              <div>
                <Paragraph ellipsis={{ rows: 2 }}>{topic.description}</Paragraph>
                <Text type="secondary">
                  by {topic.created_by?.first_name} {topic.created_by?.last_name} • {dayjs(topic.created_at).fromNow()}
                </Text>
              </div>
            }
          />
        </List.Item>
      )}
    />
  );

  const renderMessages = () => (
    <div style={{ height: "400px", overflowY: "auto", padding: "16px" }}>
      {messagesLoading ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      ) : messages.length === 0 ? (
        <Empty description="No messages yet. Start the conversation!" />
      ) : (
        <List
          itemLayout="horizontal"
          dataSource={messages}
          renderItem={(msg) => (
            <List.Item>
              <List.Item.Meta
                avatar={<Avatar icon={<UserOutlined />} />}
                title={
                  <Space>
                    <span>{msg.user?.first_name} {msg.user?.last_name}</span>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      {dayjs(msg.created_at).fromNow()}
                    </Text>
                  </Space>
                }
                description={
                  <div>
                    <Paragraph style={{ marginBottom: 8 }}>{msg.message}</Paragraph>
                    {msg.reactions && msg.reactions.length > 0 && (
                      <Space>
                        {msg.reactions.map((reaction, index) => (
                          <Tag key={index} icon={<LikeOutlined />} color="blue">
                            {reaction.count}
                          </Tag>
                        ))}
                      </Space>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <div style={{ padding: "24px" }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card style={{ marginBottom: 16 }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={2} style={{ margin: 0 }}>
                  <MessageOutlined style={{ marginRight: 8 }} />
                  Staff Discussion Forum
                </Title>
                <Text type="secondary">
                  Secure communication platform for team collaboration
                </Text>
              </Col>
              <Col>
                <Space>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setIsTopicModalVisible(true)}
                  >
                    New Topic
                  </Button>
                  <Button icon={<ReloadOutlined />} onClick={fetchTopics}>
                    Refresh
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={6}>
          <Card 
            title={
              <Space>
                <UserOutlined />
                <span>Online Users</span>
                <Badge count={onlineUsers.filter(u => u.status === "online").length} />
              </Space>
            }
            size="small"
            style={{ marginBottom: 16 }}
          >
            <List
              size="small"
              dataSource={onlineUsers}
              renderItem={(user) => (
                <List.Item>
                  <Space>
                    <Badge 
                      status={user.status === "online" ? "success" : "default"}
                      dot
                    />
                    <span>{user.name}</span>
                  </Space>
                </List.Item>
              )}
            />
          </Card>

          <Alert
            icon={<SafetyOutlined />}
            message="Secure Communication"
            description="All messages are encrypted and monitored for security. Please maintain professional conduct and respect confidentiality guidelines."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        </Col>

        <Col xs={24} lg={18}>
          {!selectedTopic ? (
            <Card 
              title={
                <Space>
                  <MessageOutlined />
                  <span>Discussion Topics</span>
                  <Badge count={topics.length} />
                </Space>
              }
              loading={loading}
            >
              {loading ? (
                <div style={{ textAlign: "center", padding: "50px" }}>
                  <Spin size="large" />
                </div>
              ) : topics.length === 0 ? (
                <Empty description="No topics yet. Create the first topic to start discussions!" />
              ) : (
                renderTopicsList()
              )}
            </Card>
          ) : (
            <Card
              title={
                <Space>
                  <Button 
                    type="text" 
                    onClick={() => {
                      // Update presence to show user left the topic
                      if (user && company && selectedTopic) {
                        discussionService.updateUserPresence(user.id, company.id, 'online', null, token);
                      }
                      setSelectedTopic(null);
                    }}
                  >
                    ← Back to Topics
                  </Button>
                  <Divider type="vertical" />
                  <span>{selectedTopic.title}</span>
                  <Tag color={getCategoryColor(selectedTopic.category)}>
                    {getCategoryLabel(selectedTopic.category)}
                  </Tag>
                </Space>
              }
              extra={
                <Space>
                  <Badge count={selectedTopic.message_count} />
                  <Button icon={<EyeOutlined />} size="small">
                    {selectedTopic.message_count} messages
                  </Button>
                </Space>
              }
            >
              {renderMessages()}
              
              <Divider />
              
              <div style={{ display: "flex", gap: "8px" }}>
                <Input.TextArea
                  value={newMessage}
                  onChange={handleMessageInputChange}
                  placeholder="Type your message..."
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  onPressEnter={(e) => {
                    if (e.shiftKey) return;
                    e.preventDefault();
                    handleSendMessage();
                  }}
                />
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                  >
                    Send
                  </Button>
                  <Button
                    type="text"
                    icon={<PaperClipOutlined />}
                    size="small"
                    title="Attach file"
                  />
                </div>
              </div>
            </Card>
          )}
        </Col>
      </Row>

      <Modal
        title="Create New Topic"
        open={isTopicModalVisible}
        onCancel={() => {
          setIsTopicModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateTopic}
        >
          <Form.Item
            name="title"
            label="Topic Title"
            rules={[
              { required: true, message: "Please enter a topic title" },
              { min: 5, message: "Title must be at least 5 characters" }
            ]}
          >
            <Input placeholder="Enter topic title" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { required: true, message: "Please enter a description" },
              { min: 10, message: "Description must be at least 10 characters" }
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Describe what this topic is about..."
            />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: "Please select a category" }]}
          >
            <Select placeholder="Select category">
              {categories.map(cat => (
                <Option key={cat.value} value={cat.value}>
                  <Tag color={cat.color}>{cat.label}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Create Topic
              </Button>
              <Button onClick={() => {
                setIsTopicModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DiscussionForum;